"use strict";

const hero = document.querySelector("[data-nua-hero-3d]");
const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

if (hero) {
  hero.dataset.hero3dStatus = reduceMotion.matches ? "reduced-motion" : "loading";
  if (!reduceMotion.matches) {
    initSpatialHero(hero).catch(() => {
      hero.classList.add("hero-3d-unavailable");
      hero.dataset.hero3dStatus = "photo-depth";
      initPhotoDepthFallback(hero);
    });
  }
}

async function initSpatialHero(root) {
  const canvas = root.querySelector("[data-hero-canvas]");
  const fallbackImage = root.querySelector("picture img");
  if (!canvas || !fallbackImage) throw new Error("hero_missing");

  try {
    await fallbackImage.decode();
  } catch {
    if (!fallbackImage.complete) return;
  }

  const textureUrl = fallbackImage.currentSrc || fallbackImage.src;
  if (!textureUrl) return;

  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: window.devicePixelRatio < 2,
    alpha: false,
    powerPreference: "high-performance",
    preserveDrawingBuffer: true,
  });

  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
  if ("outputColorSpace" in renderer) renderer.outputColorSpace = THREE.SRGBColorSpace;
  else renderer.outputEncoding = THREE.sRGBEncoding;

  const textureLoader = new THREE.TextureLoader();
  textureLoader.setCrossOrigin("anonymous");
  const texture = await textureLoader.loadAsync(textureUrl);
  if ("colorSpace" in texture) texture.colorSpace = THREE.SRGBColorSpace;
  else texture.encoding = THREE.sRGBEncoding;
  texture.anisotropy = Math.min(4, renderer.capabilities.getMaxAnisotropy());

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x171717);

  const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 10);
  camera.position.z = 2;

  const strength = THREE.MathUtils.clamp(Number(root.dataset.motionStrength) || 6, 2, 10) / 6;
  const uniforms = {
    uTexture: { value: texture },
    uTime: { value: 0 },
    uPointer: { value: new THREE.Vector2() },
    uUvScale: { value: new THREE.Vector2(1, 1) },
    uStrength: { value: strength },
    uScroll: { value: 0 },
  };

  const geometry = new THREE.PlaneGeometry(1, 1, 48, 30);
  const material = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: `
      uniform float uTime;
      uniform float uStrength;
      uniform float uScroll;
      uniform vec2 uPointer;
      varying vec2 vUv;

      void main() {
        vUv = uv;
        vec3 transformed = position;
        float edgeFade = sin(uv.x * 3.14159265) * sin(uv.y * 3.14159265);
        float fabricWave = sin((uv.x * 6.8) + (uTime * 0.28))
          * sin((uv.y * 4.4) - (uTime * 0.16))
          * 0.012;
        float pointerDepth = ((uv.x - 0.5) * uPointer.x - (uv.y - 0.5) * uPointer.y) * 0.046;

        transformed.z += (fabricWave * edgeFade + pointerDepth) * uStrength;
        transformed.x += uPointer.x * (uv.y - 0.5) * 0.008 * uStrength;
        transformed.y += uScroll * (uv.x - 0.5) * 0.012;

        gl_Position = projectionMatrix * modelViewMatrix * vec4(transformed, 1.0);
      }
    `,
    fragmentShader: `
      uniform sampler2D uTexture;
      uniform vec2 uUvScale;
      varying vec2 vUv;

      void main() {
        vec2 coverUv = (vUv - 0.5) * uUvScale + 0.5;
        gl_FragColor = texture2D(uTexture, coverUv);
      }
    `,
  });

  const imagePlane = new THREE.Mesh(geometry, material);
  scene.add(imagePlane);

  const pointerTarget = new THREE.Vector2();
  const pointerCurrent = new THREE.Vector2();
  let scrollTarget = 0;
  let scrollCurrent = 0;
  let visible = true;
  let animationFrame = 0;
  let lastTime = performance.now();

  function resize() {
    const width = Math.max(1, root.clientWidth);
    const height = Math.max(1, root.clientHeight);
    const viewportAspect = width / height;
    const imageWidth = texture.image.naturalWidth || texture.image.videoWidth || texture.image.width;
    const imageHeight = texture.image.naturalHeight || texture.image.videoHeight || texture.image.height;
    const imageAspect = imageWidth / imageHeight;

    renderer.setSize(width, height, false);
    camera.aspect = viewportAspect;
    camera.updateProjectionMatrix();

    const viewHeight = 2 * Math.tan(THREE.MathUtils.degToRad(camera.fov * 0.5)) * camera.position.z;
    const overscan = 1.055;
    imagePlane.scale.set(viewHeight * viewportAspect * overscan, viewHeight * overscan, 1);

    if (viewportAspect > imageAspect) {
      uniforms.uUvScale.value.set(1, imageAspect / viewportAspect);
    } else {
      uniforms.uUvScale.value.set(viewportAspect / imageAspect, 1);
    }
  }

  function updateScroll() {
    const bounds = root.getBoundingClientRect();
    scrollTarget = THREE.MathUtils.clamp(-bounds.top / Math.max(bounds.height, 1), 0, 1);
  }

  function onPointerMove(event) {
    const bounds = root.getBoundingClientRect();
    pointerTarget.set(
      THREE.MathUtils.clamp(((event.clientX - bounds.left) / bounds.width) * 2 - 1, -1, 1),
      THREE.MathUtils.clamp(-(((event.clientY - bounds.top) / bounds.height) * 2 - 1), -1, 1),
    );
  }

  function onPointerLeave() {
    pointerTarget.set(0, 0);
  }

  function render(now) {
    animationFrame = 0;
    if (!visible || document.hidden) return;

    const delta = Math.min((now - lastTime) / 1000, 0.05);
    lastTime = now;
    pointerCurrent.lerp(pointerTarget, 0.055);
    scrollCurrent = THREE.MathUtils.lerp(scrollCurrent, scrollTarget, 0.045);

    uniforms.uTime.value += delta;
    uniforms.uPointer.value.copy(pointerCurrent);
    uniforms.uScroll.value = scrollCurrent;

    imagePlane.rotation.x = pointerCurrent.y * 0.01 * strength;
    imagePlane.rotation.y = -pointerCurrent.x * 0.013 * strength;
    imagePlane.position.y = scrollCurrent * 0.025;
    camera.position.z = 2 + scrollCurrent * 0.035;

    renderer.render(scene, camera);
    animationFrame = requestAnimationFrame(render);
  }

  function start() {
    if (!animationFrame && visible && !document.hidden) {
      lastTime = performance.now();
      animationFrame = requestAnimationFrame(render);
    }
  }

  function stop() {
    if (animationFrame) cancelAnimationFrame(animationFrame);
    animationFrame = 0;
  }

  if ("ResizeObserver" in window) {
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(root);
  } else {
    window.addEventListener("resize", resize, { passive: true });
  }

  if ("IntersectionObserver" in window) {
    const visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        if (visible) start();
        else stop();
      },
      { rootMargin: "120px 0px" },
    );
    visibilityObserver.observe(root);
  }

  root.addEventListener("pointermove", onPointerMove, { passive: true });
  root.addEventListener("pointerleave", onPointerLeave, { passive: true });
  window.addEventListener("scroll", updateScroll, { passive: true });
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) stop();
    else start();
  });

  canvas.addEventListener(
    "webglcontextlost",
    (event) => {
      event.preventDefault();
      stop();
      root.classList.remove("hero-3d-ready");
      root.classList.add("hero-3d-unavailable");
    },
    { once: true },
  );

  resize();
  updateScroll();
  renderer.render(scene, camera);
  root.classList.add("hero-3d-ready");
  root.dataset.hero3dStatus = "ready";
  start();
}

function initPhotoDepthFallback(root) {
  const picture = root.querySelector("picture");
  if (!picture) return;

  root.classList.add("hero-photo-depth");

  root.addEventListener(
    "pointermove",
    (event) => {
      const bounds = root.getBoundingClientRect();
      const x = THREE.MathUtils.clamp(((event.clientX - bounds.left) / bounds.width) * 2 - 1, -1, 1);
      const y = THREE.MathUtils.clamp(((event.clientY - bounds.top) / bounds.height) * 2 - 1, -1, 1);
      picture.style.setProperty("--hero-depth-x", `${(-x * 0.55).toFixed(3)}%`);
      picture.style.setProperty("--hero-depth-y", `${(-y * 0.35).toFixed(3)}%`);
      picture.style.setProperty("--hero-depth-ry", `${(x * 0.18).toFixed(3)}deg`);
      picture.style.setProperty("--hero-depth-rx", `${(-y * 0.12).toFixed(3)}deg`);
    },
    { passive: true },
  );

  root.addEventListener(
    "pointerleave",
    () => {
      picture.style.setProperty("--hero-depth-x", "0%");
      picture.style.setProperty("--hero-depth-y", "0%");
      picture.style.setProperty("--hero-depth-ry", "0deg");
      picture.style.setProperty("--hero-depth-rx", "0deg");
    },
    { passive: true },
  );
}
